import { Box, Tooltip, Typography } from '@mui/material'
import {
  differenceInCalendarDays, format, isValid, parseISO
} from 'date-fns'
import { de } from 'date-fns/locale'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { TOKENS } from '../../../../pages/dashboard-v2/components/primitives'
import { initials, tileColors } from '../../../../util/tile'
import { Assignee, FolderChecklist } from '../../../../types/FolderChecklist'

// Cells of the process list in the new design (redesign round 1, behind use_new_design).
// The table, its actions menu, selection, sorting and paging stay the old DataTable.

type Tone = 'neutral' | 'primary' | 'danger' | 'success' | 'warning'

const TONES: Record<Tone, { bg: string, fg: string }> = {
  neutral: { bg: '#F1F3F6', fg: TOKENS.muted },
  primary: { bg: TOKENS.primarySoft, fg: TOKENS.primaryDark },
  danger: { bg: '#FDECEC', fg: '#A3261F' },
  success: { bg: '#E8F5EE', fg: '#1E7A4C' },
  warning: { bg: '#FFF4E0', fg: '#8A5300' }
}

export function Chip({ children, tone = 'neutral' }: { children: ReactNode, tone?: Tone }) {
  const colors = TONES[tone]
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        px: 1,
        borderRadius: '999px',
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        bgcolor: colors.bg,
        color: colors.fg
      }}
    >
      {children}
    </Box>
  )
}

function toDate(value?: string | Date | null): Date | null {
  if (!value) return null
  const date = value instanceof Date ? value : parseISO(String(value).replace(/ UTC$/, 'Z').replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T'))
  return isValid(date) ? date : null
}

interface TitleProps {
  title: string
  to: string
  sub?: string | null
  offline?: boolean
  // phones: due date, progress and notes inside the card (the other columns are hidden)
  extra?: ReactNode
  // instead of following the link (e.g. "Geplant": open the schedule)
  onOpen?: () => void
}

export function TitleCell({
  title, to, sub, offline, extra, onOpen
}: TitleProps) {
  const [bg, fg] = tileColors(title ?? '')
  return (
    <Box sx={{
      display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0
    }}
    >
      <Box
        aria-hidden
        sx={{
          flex: 'none',
          width: 36,
          height: 36,
          borderRadius: '9px',
          bgcolor: bg,
          color: fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700
        }}
      >
        {initials(title ?? '')}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box
          component={Link}
          to={to}
          // the row navigates on click too; without this the same page lands twice in the history
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            if (onOpen) {
              e.preventDefault()
              onOpen()
            }
          }}
          sx={{
            display: 'block',
            fontSize: 14,
            fontWeight: 600,
            color: TOKENS.text,
            textDecoration: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: { xs: 'normal', md: 'nowrap' },
            '&:hover': { color: TOKENS.primary }
          }}
        >
          {title}
          {offline && <i className="fas fa-download" style={{ marginLeft: 6, fontSize: 11, color: TOKENS.muted }} aria-hidden />}
        </Box>
        {sub && (
          <Typography sx={{
            fontSize: 12.5, color: TOKENS.muted, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}
          >
            {sub}
          </Typography>
        )}
        {extra}
      </Box>
    </Box>
  )
}

// "überfällig seit 2 Tagen", "heute 14:00", "morgen 07:00", "Freitag 12:00", "24.11.2026"
export function DueCell({ due, overdue, short }: { due?: string | null, overdue?: boolean, short?: boolean }) {
  const { t } = useTranslation()
  const date = toDate(due)
  if (!date) return null
  const now = new Date()
  const days = differenceInCalendarDays(date, now)
  const time = format(date, 'HH:mm')
  let label: string
  let color = TOKENS.text
  if (overdue || date < now) {
    const ago = -days
    color = '#A3261F'
    if (ago <= 0) {
      const hours = Math.max(1, Math.round((now.getTime() - date.getTime()) / 3600000))
      label = t(short ? 'processList.sinceHours' : 'processList.overdueHours', { count: hours })
    } else {
      label = t(short ? 'processList.sinceDays' : 'processList.overdueDays', { count: ago })
    }
  } else if (days === 0) {
    color = '#8A5300'
    label = short ? time : t('processList.today', { time })
  } else if (days === 1) {
    label = t('processList.tomorrow', { time })
  } else if (days < 7) {
    label = short ? format(date, 'EEEE', { locale: de }) : `${format(date, 'EEEE', { locale: de })} ${time}`
  } else {
    label = format(date, 'dd.MM.yyyy')
  }
  return (
    <Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: TOKENS.muted }}>{format(date, 'dd.MM. HH:mm')}</Typography>
    </Box>
  )
}

export function DateCell({ value }: { value?: string | null }) {
  const date = toDate(value)
  if (!date) return null
  return (
    <Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{format(date, 'dd.MM.yyyy')}</Typography>
      <Typography sx={{ fontSize: 12, color: TOKENS.muted }}>{format(date, 'HH:mm')}</Typography>
    </Box>
  )
}

// responsible teams and people as small avatars (draft "Main"); every name on hover
type Responsible = FolderChecklist['responsible']

// nobody assigned directly: who is responsible in practice (follow-up schedule, template or
// creator) as dashed badges; in works council mode without a person a "?"
function ResponsibleFallback({ responsible }: { responsible: NonNullable<Responsible> }) {
  const { t } = useTranslation()
  const people = responsible.people ?? []
  const title = people.length && responsible.source
    ? t(`processList.responsibleFrom.${responsible.source}`, { names: people.map((p) => p.name).join(', ') })
    : t('processList.notAssigned')
  const badge = (p: { name: string, url: string | null, type: string } | null, i: number) => {
    const [bg, fg] = p ? tileColors(p.name ?? '') : ['#FFFFFF', TOKENS.muted]
    return (
      <Box
        key={p ? `${p.type}-${p.name}` : 'none'}
        sx={{
          width: 28,
          height: 28,
          flex: 'none',
          ml: i ? '-6px' : 0,
          borderRadius: p?.type === 'team' ? '8px' : '50%',
          border: `1.5px dashed ${p ? fg : '#8A94A6'}`,
          bgcolor: bg,
          color: fg,
          fontSize: 11,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: p?.url ? `url(${p.url})` : 'none',
          backgroundSize: 'cover'
        }}
      >
        {p ? (p.url ? null : initials(p.name ?? '')) : '?'}
      </Box>
    )
  }
  const shown = people.slice(0, 3)
  return (
    <Tooltip title={title} arrow placement="top">
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, opacity: 0.9 }}>
        {shown.length ? shown.map(badge) : badge(null, 0)}
        {people.length > 3 && (
          <Typography component="span" sx={{ ml: 0.75, fontSize: 12.5, fontWeight: 600, color: TOKENS.muted }}>
            +{people.length - 3}
          </Typography>
        )}
      </Box>
    </Tooltip>
  )
}

export function AssigneesCell({ assignees, responsible }: { assignees?: Assignee[] | null, responsible?: Responsible }) {
  if (!assignees?.length) return responsible ? <ResponsibleFallback responsible={responsible} /> : null
  const shown = assignees.slice(0, 3)
  const more = assignees.length - shown.length
  const names = (
    <Box component="span" sx={{ display: 'block', whiteSpace: 'pre-line' }}>
      {assignees.map((a) => (a.type === 'team' ? `${a.name} (Team)` : a.name)).join('\n')}
    </Box>
  )
  const avatar = (a: Assignee, i: number) => {
    const [bg, fg] = tileColors(a.name ?? '')
    return (
      <Box
        key={`${a.type}-${a.name}-${i}`}
        sx={{
          width: 28,
          height: 28,
          flex: 'none',
          ml: i ? '-6px' : 0,
          borderRadius: a.type === 'team' ? '8px' : '50%',
          border: '2px solid #FFFFFF',
          bgcolor: bg,
          color: fg,
          fontSize: 11,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: a.url ? `url(${a.url})` : 'none',
          backgroundSize: 'cover'
        }}
      >
        {a.url ? null : initials(a.name ?? '')}
      </Box>
    )
  }
  return (
    <Tooltip title={names} arrow placement="top">
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        {shown.map(avatar)}
        {more > 0 && (
          <Typography component="span" sx={{ ml: 0.75, fontSize: 12.5, fontWeight: 600, color: TOKENS.muted }}>
            +{more}
          </Typography>
        )}
      </Box>
    </Tooltip>
  )
}

// small completion ring for the condensed list next to the open process (like the process header)
export function ProgressRing({ progress, size = 30 }: { progress?: number | null, size?: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(progress ?? 0)))
  const r = (size - 4) / 2
  const c = 2 * Math.PI * r
  const color = pct === 100 ? '#1E7A4C' : TOKENS.primary
  return (
    <Box component="span" sx={{ position: 'relative', width: size, height: size, flex: 'none', display: 'inline-flex' }} title={`${pct} %`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#D7DDE6" strokeWidth="3" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${(c * pct) / 100} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Box
        component="span"
        sx={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: TOKENS.text
        }}
      >
        {pct}
      </Box>
    </Box>
  )
}

// aside: shown at the right end of the text line (phone cards put the due date there)
export function ProgressCell({
  progress, answered, total, aside
}: { progress?: number | null, answered?: number | null, total?: number | null, aside?: ReactNode }) {
  const { t } = useTranslation()
  const pct = Math.max(0, Math.min(100, Math.round(progress ?? 0)))
  return (
    <Box sx={{ minWidth: 90 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {total ? t('processList.answered', { answered: answered ?? 0, total }) : `${pct} %`}
        </Typography>
        {aside}
      </Box>
      <Box sx={{
        height: 6, borderRadius: '3px', bgcolor: '#D7DDE6', mt: 0.75, overflow: 'hidden'
      }}
      >
        <Box sx={{
          height: 6, width: `${pct}%`, bgcolor: pct === 100 ? '#1E7A4C' : TOKENS.primary
        }}
        />
      </Box>
    </Box>
  )
}

interface HintsProps {
  closed: boolean
  locked?: boolean
  deviations?: number | null
  tasks?: number | null
  offline?: boolean
  progress?: number | null
}

export function HintsCell({
  closed, locked, deviations, tasks, offline, progress
}: HintsProps) {
  const { t } = useTranslation()
  const chips: ReactNode[] = []
  if (deviations) chips.push(<Chip key="dev" tone="danger">{t('processList.deviations', { count: deviations })}</Chip>)
  if (closed && !deviations) chips.push(<Chip key="ok" tone="success">{t('processList.noDeviations')}</Chip>)
  if (tasks) chips.push(<Chip key="tasks" tone="primary">{t('processList.tasks', { count: tasks })}</Chip>)
  if (!closed && offline) chips.push(<Chip key="offline">{t('processList.offline')}</Chip>)
  // past due and locked by the template: nobody can work on it or close it any more
  if (!closed && locked) chips.push(<Chip key="locked" tone="danger">{t('processList.locked')}</Chip>)
  if (!closed && !locked && (progress ?? 0) >= 100) chips.push(<Chip key="ready" tone="success">{t('processList.readyToClose')}</Chip>)
  return <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>{chips}</Box>
}

// phones: one line with the due date and progress, then the notes
export function PhoneMeta({
  due, overdue, closedAt, progress, answered, total, hints
}: {
  due?: string | null, overdue?: boolean, closedAt?: string | null, progress?: number | null,
  answered?: number | null, total?: number | null, hints: ReactNode
}) {
  const { t } = useTranslation()
  const closed = toDate(closedAt)
  return (
    <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{
        display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap'
      }}
      >
        {closed
          ? <Typography sx={{ fontSize: 12.5, color: TOKENS.muted }}>{format(closed, 'dd.MM.yyyy HH:mm')}</Typography>
          : <Box sx={{ '& p:last-of-type': { display: 'none' } }}><DueCell due={due} overdue={overdue} /></Box>}
        {!closed && total ? (
          <Typography sx={{ fontSize: 12.5, color: TOKENS.muted }}>
            {t('processList.answered', { answered: answered ?? 0, total })}
          </Typography>
        ) : (!closed && <Typography sx={{ fontSize: 12.5, color: TOKENS.muted }}>{`${Math.round(progress ?? 0)} %`}</Typography>)}
      </Box>
      {hints}
    </Box>
  )
}
